import { cn } from "@/lib/utils";

interface SectionHeaderProps {
  eyebrow: string;
  heading: React.ReactNode;
  body?: React.ReactNode;
  id?: string;
  as?: "h1" | "h2" | "h3";
  align?: "left" | "center";
  headingClassName?: string;
  bodyClassName?: string;
  /* Stripe-style two-tone heading: `body` renders INSIDE the heading in muted
     ink instead of as a separate paragraph below it, so the statement and its
     qualifying sentence read as one block. Opt-in — pages that want the
     classic eyebrow / heading / lead stack simply omit it. */
  inlineBody?: boolean;
}

export default function SectionHeader({
  eyebrow,
  heading,
  body,
  id,
  as: Tag = "h2",
  align = "left",
  headingClassName,
  bodyClassName,
  inlineBody = false,
}: SectionHeaderProps) {
  return (
    <>
      {/* Colored eyebrow — on the PRIMARY accent (blue). Section labels are
         structural page furniture, so they carry the lead colour; orange is
         reserved for sparing accents. brand-green flips per theme
         (#5b8cff → #2563eb), so it stays readable on both grounds. */}
      <p
        className={cn(
          "reveal-up text-brand-green mb-3 text-sm font-medium",
          align === "center" && "text-center"
        )}
      >
        {eyebrow}
      </p>
      <Tag
        /* With an inline body the id moves to the statement span, so a section
           pointing at it via aria-labelledby keeps a short accessible name
           instead of inheriting the whole qualifying sentence. */
        id={inlineBody ? undefined : id}
        className={cn(
          "reveal-up text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl",
          align === "center" && "text-center",
          headingClassName
        )}
      >
        {inlineBody ? <span id={id}>{heading}</span> : heading}
        {inlineBody && body && <span className="text-muted-foreground"> {body}</span>}
      </Tag>
      {body && !inlineBody && (
        <p
          className={cn(
            "reveal-up text-muted-foreground text-lg leading-relaxed",
            align === "center" && "mx-auto max-w-xl",
            bodyClassName
          )}
        >
          {body}
        </p>
      )}
    </>
  );
}
